import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listCaseRequestInvolvement,
  type CaseRequestInvolvementRow,
} from '../../services/caseRequestInvolvement';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';

const EMPTY = '—';

function actionPillClass(value: string): string {
  const s = value.trim().toLowerCase();
  if (s.includes('case submitted')) return 'inv-pill is-gray';
  if (s.includes('check-in') || s.includes('check in')) return 'inv-pill is-blue';
  if (s.includes('allocated')) return 'inv-pill is-teal';
  if (s.includes('assigned')) return 'inv-pill is-green';
  if (s.includes('minutes')) return 'inv-pill is-purple';
  if (s.includes('subtask')) return 'inv-pill is-gold';
  return 'inv-pill is-slate';
}

function formatDateTime(value: string | null | undefined): string {
  if (!value?.trim()) return EMPTY;
  return formatDisplayDateTime(value) || value.trim();
}

function sortRows(rows: CaseRequestInvolvementRow[]): CaseRequestInvolvementRow[] {
  return [...rows].sort((a, b) => {
    const aTime = Date.parse(a.dateTime);
    const bTime = Date.parse(b.dateTime);
    if (Number.isFinite(aTime) && Number.isFinite(bTime)) return bTime - aTime;
    return b.dateTime.localeCompare(a.dateTime);
  });
}

function displayUserName(row: CaseRequestInvolvementRow): string {
  const primary = row.userDisplayName.trim();
  const secondary = row.userName.trim();
  if (primary && secondary && primary !== secondary) return `${primary} - ${secondary}`;
  return primary || secondary || EMPTY;
}

type Props = {
  caseRequestId: string;
  title?: string;
};

export default function TaskCaseInvolvementTab({ caseRequestId, title = 'Involvement Details' }: Props) {
  const { t } = useTranslation();
  const canLoad = isCaseRequestUuid(caseRequestId);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [rows, setRows] = useState<CaseRequestInvolvementRow[]>([]);

  useEffect(() => {
    if (!canLoad) return;
    const controller = new AbortController();

    setLoading(true);
    setLoadError('');
    void listCaseRequestInvolvement({ caseRequestId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setLoading(false);
        if (!res.success) {
          setLoadError(res.message ?? 'Failed to load involvement details.');
          return;
        }
        setRows(sortRows(res.data.involvementDetails));
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setLoadError('Failed to load involvement details.');
        }
      });

    return () => controller.abort();
  }, [canLoad, caseRequestId]);

  if (!canLoad) return null;

  return (
    <div className="inv-sec">
      <div className="inv-head">
        <div className="inv-title-row">
          <div className="inv-title">{title}</div>
          {!loading && !loadError ? <span className="inv-count">{rows.length} records</span> : null}
        </div>
      </div>

      {loading ? (
        <p className="tcd-loading">Loading involvement details…</p>
      ) : loadError ? (
        <p className="tcd-error" role="alert">
          {loadError}
        </p>
      ) : rows.length ? (
        <div className="tw inv-tbl-wrap">
          <table className="inv-tbl">
            <thead>
              <tr>
                <th>User's Name</th>
                <th>User Role</th>
                <th>Action / Involvement</th>
                <th>Date &amp; Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || `${row.userDisplayName}-${row.dateTime}-${row.actionDisplayName}`}>
                  <td>
                    <div className="inv-user-name">{displayUserName(row)}</div>
                  </td>
                  <td>
                    <div className="inv-role">{row.allUserRoles || EMPTY}</div>
                  </td>
                  <td>
                    <span className={actionPillClass(row.actionDisplayName)}>{row.actionDisplayName || row.actionCode || EMPTY}</span>
                  </td>
                  <td>
                    <div className="inv-date">{formatDateTime(row.dateTime)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="tcd-doc-empty-msg">{t('common.noResultsFound')}</p>
      )}
    </div>
  );
}